/**
 * Training Management Page (Admin)
 *
 * Admin page for managing training courses, sessions, and enrollments.
 * Allows admins to create/edit courses, schedule sessions, and view enrollment statistics.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Calendar,
  Users,
  Award,
  Plus,
  Search,
  Filter as FilterIcon,
  Edit,
  Trash2,
} from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Table } from '../../components/common/Table';
import { EmptyState } from '../../components/common/EmptyState';
import { TableSkeleton } from '../../components/common/Skeleton';
import { useToastHelpers } from '../../components/feedback';
import { mockApi } from '../../services/mockApi';
import type { TrainingCourse, TrainingSession } from '../../types/training';
import { TrainingCategory, TrainingStatus } from '../../types/training';
import { formatDate } from '../../utils/formatting';

export const TrainingManagementPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const { success, error: showError } = useToastHelpers();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'courses' | 'sessions'>('courses');
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [coursesResponse, sessionsResponse] = await Promise.all([
        mockApi.getTrainingCourses(),
        mockApi.getTrainingSessions(),
      ]);

      if (coursesResponse.success && coursesResponse.data) {
        setCourses(coursesResponse.data);
      }

      if (sessionsResponse.success && sessionsResponse.data) {
        setSessions(sessionsResponse.data);
      }
    } catch (err) {
      console.error('Error loading training data:', err);
      showError('Failed to load training data', 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = (id: string) => {
    const course = courses.find((c) => c.id === id);
    if (window.confirm(`Are you sure you want to delete "${course?.title}"?`)) {
      success(`Course "${course?.title}" has been deleted`, 'Course Deleted');
      setCourses(courses.filter((c) => c.id !== id));
    }
  };

  const handleDeleteSession = (id: string) => {
    const session = sessions.find((s) => s.id === id);
    if (window.confirm(`Are you sure you want to delete this session?`)) {
      success(`Session for "${session?.courseName}" has been deleted`, 'Session Deleted');
      setSessions(sessions.filter((s) => s.id !== id));
    }
  };

  const filteredCourses = courses.filter((course) => {
    const matchesSearch =
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredSessions = sessions.filter((session) =>
    session.courseName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalEnrollments = sessions.reduce((sum, s) => sum + s.enrolledCount, 0);
  const upcomingSessions = sessions.filter((s) => s.status === TrainingStatus.UPCOMING);

  return (
    <PageLayout
      pageTitle="Training Management"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => alert('Create New Session')}
          >
            New Session
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => alert('Create New Course')}
          >
            New Course
          </Button>
        </div>
      }
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: 'Admin', href: '/admin/dashboard' },
        { label: 'Training Management' },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Courses</p>
                <p className="text-2xl font-bold text-primary-600">{courses.length}</p>
              </div>
              <BookOpen className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Upcoming Sessions</p>
                <p className="text-2xl font-bold text-info-600">{upcomingSessions.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-info-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Enrollments</p>
                <p className="text-2xl font-bold text-success-600">{totalEnrollments}</p>
              </div>
              <Users className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Certificates Issued</p>
                <p className="text-2xl font-bold text-warning-600">
                  {courses.filter((c) => c.certificateIssued).length}
                </p>
              </div>
              <Award className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-neutral-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'courses'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                <span>Courses ({courses.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'sessions'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>Sessions ({sessions.length})</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {activeTab === 'courses' && (
              <div className="flex items-center gap-2">
                <FilterIcon className="w-5 h-5 text-gray-400" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Categories</option>
                  <option value={TrainingCategory.HALAL_AWARENESS}>Halal Awareness</option>
                  <option value={TrainingCategory.HALAL_EXECUTIVE}>Halal Executive</option>
                  <option value={TrainingCategory.HALAL_SUPERVISOR}>Halal Supervisor</option>
                  <option value={TrainingCategory.INTERNAL_AUDITOR}>Internal Auditor</option>
                  <option value={TrainingCategory.SLAUGHTERING}>Slaughtering</option>
                </select>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Content */}
      {loading ? (
        <TableSkeleton rows={5} />
      ) : activeTab === 'courses' ? (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Training Courses</h3>
          </CardHeader>
          <CardBody>
            {filteredCourses.length === 0 ? (
              <EmptyState
                icon={<BookOpen className="w-10 h-10" />}
                title="No Courses Found"
                description="No training courses match your search criteria."
                action={{
                  label: 'Create New Course',
                  onClick: () => alert('Create Course'),
                  icon: <Plus className="w-4 h-4" />,
                }}
              />
            ) : (
              <Table
                columns={[
                  {
                    key: 'title',
                    header: 'Course Title',
                    render: (course: TrainingCourse) => (
                      <div>
                        <div className="font-medium text-gray-900">{course.title}</div>
                        <div className="text-xs text-gray-500">{course.category.replace(/_/g, ' ')}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'duration',
                    header: 'Duration',
                    render: (course: TrainingCourse) => (
                      <span className="text-sm text-gray-700">{course.durationHours}h</span>
                    ),
                  },
                  {
                    key: 'instructors',
                    header: 'Instructors',
                    render: (course: TrainingCourse) => (
                      <div className="text-sm text-gray-700">
                        {course.instructors.join(', ')}
                      </div>
                    ),
                  },
                  {
                    key: 'certificate',
                    header: 'Certificate',
                    render: (course: TrainingCourse) =>
                      course.certificateIssued ? (
                        <Badge variant="success" size="sm">Yes</Badge>
                      ) : (
                        <Badge variant="default" size="sm">No</Badge>
                      ),
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    render: (course: TrainingCourse) => (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Edit className="w-4 h-4" />}
                          onClick={() => alert(`Edit ${course.title}`)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          icon={<Trash2 className="w-4 h-4" />}
                          onClick={() => handleDeleteCourse(course.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    ),
                  },
                ]}
                data={filteredCourses}
                keyExtractor={(course) => course.id}
              />
            )}
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Training Sessions</h3>
          </CardHeader>
          <CardBody>
            {filteredSessions.length === 0 ? (
              <EmptyState
                icon={<Calendar className="w-10 h-10" />}
                title="No Sessions Found"
                description="No training sessions match your search criteria."
                action={{
                  label: 'Schedule New Session',
                  onClick: () => alert('Create Session'),
                  icon: <Plus className="w-4 h-4" />,
                }}
              />
            ) : (
              <Table
                columns={[
                  {
                    key: 'course',
                    header: 'Course',
                    render: (session: TrainingSession) => (
                      <div>
                        <div className="font-medium text-gray-900">{session.courseName}</div>
                        <div className="text-xs text-gray-500">{formatDate(session.startDate)}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'instructor',
                    header: 'Instructor',
                    render: (session: TrainingSession) => (
                      <span className="text-sm text-gray-700">{session.instructorName}</span>
                    ),
                  },
                  {
                    key: 'location',
                    header: 'Location',
                    render: (session: TrainingSession) => (
                      <span className="text-sm text-gray-700">{session.location || 'TBD'}</span>
                    ),
                  },
                  {
                    key: 'enrollment',
                    header: 'Enrollment',
                    render: (session: TrainingSession) => (
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">{session.enrolledCount}</span>
                        <span className="text-gray-500"> / {session.maxParticipants}</span>
                      </div>
                    ),
                  },
                  {
                    key: 'fee',
                    header: 'Fee',
                    render: (session: TrainingSession) => (
                      <span className="text-sm font-semibold text-primary-700">
                        {session.currency} {session.fee?.toFixed(2) || 'N/A'}
                      </span>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (session: TrainingSession) => {
                      switch (session.status) {
                        case TrainingStatus.UPCOMING:
                          return <Badge variant="info">Upcoming</Badge>;
                        case TrainingStatus.IN_PROGRESS:
                          return <Badge variant="warning">In Progress</Badge>;
                        case TrainingStatus.COMPLETED:
                          return <Badge variant="success">Completed</Badge>;
                        case TrainingStatus.CANCELLED:
                          return <Badge variant="error">Cancelled</Badge>;
                      }
                    },
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    render: (session: TrainingSession) => (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Edit className="w-4 h-4" />}
                          onClick={() => alert(`Edit session ${session.id}`)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          icon={<Trash2 className="w-4 h-4" />}
                          onClick={() => handleDeleteSession(session.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    ),
                  },
                ]}
                data={filteredSessions}
                keyExtractor={(session) => session.id}
              />
            )}
          </CardBody>
        </Card>
      )}
    </PageLayout>
  );
};

export default TrainingManagementPage;

